import {
  Box,
  Button,
  Card,
  Container,
  Grid2 as Grid,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Toast } from "react-bootstrap";
import { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Link } from "@mui/material";
import logoWhite from "../assets/logo-white.png";
import { apiMachineMachinesGet, apiMachineStatsGet } from "../api/machine/get";
import { apiMachineAuthorizeGet } from "../api/machine/get-authorize";
import { apiMachineMachineDelete } from "../api/machine/delete";
import { apiMachineNamePost } from "../api/machine/post";
import { MachineInfo, MachineStats } from "../api/types";
import { When } from "../components/logic/When";
import type { CloseableToastComponentType } from "../components/common/CloseableToast";
import { ToastContext } from "../providers/ToastProvider";

const UnauthorizedSaveErrorToast: CloseableToastComponentType = ({ bg, handleRemove }) => (
  <Toast bg={bg} onClose={() => handleRemove()} onExited={() => handleRemove()} show={true}>
    <Toast.Body>Failed to save machine name. You are not authorized to make this change.</Toast.Body>
  </Toast>
);

const SaveErrorToast: CloseableToastComponentType = ({ bg, handleRemove }) => (
  <Toast bg={bg} onClose={() => handleRemove()} onExited={() => handleRemove()} show={true}>
    <Toast.Body>Failed to save machine name. Please try again.</Toast.Body>
  </Toast>
);

const SaveExceptionToast: CloseableToastComponentType = ({ bg, handleRemove }) => (
  <Toast bg={bg} onClose={() => handleRemove()} onExited={() => handleRemove()} show={true}>
    <Toast.Body>Unexpected error saving machine name. Check console for details.</Toast.Body>
  </Toast>
);

const RemoveAuthErrorToast: CloseableToastComponentType = ({ bg, handleRemove }) => (
  <Toast bg={bg} onClose={() => handleRemove()} onExited={() => handleRemove()} show={true}>
    <Toast.Body>You are not authorized to remove machines. Only the licence owner can remove machines.</Toast.Body>
  </Toast>
);

const RemoveErrorToast: CloseableToastComponentType = ({ bg, handleRemove }) => (
  <Toast bg={bg} onClose={() => handleRemove()} onExited={() => handleRemove()} show={true}>
    <Toast.Body>Failed to remove machine. Please try again.</Toast.Body>
  </Toast>
);
const COLORS = {
  yellow: '#EBE32D',
  white: '#FFFFFF',
  grey: '#9AA0A6',
  lead: '#C9CDD1',
  line: 'rgba(255,255,255,.12)',
  cardBg: 'rgba(255,255,255,.035)',
  danger: '#E5736B',
};

export const MachinesPage = () => {
  const toastContext = useContext(ToastContext);

  const [machines, setMachines] = useState<MachineInfo[]>([]);
  const [machinesLoading, setMachinesLoading] = useState(true);
  const [stats, setStats] = useState<MachineStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  const fetchMachines = async () => {
    setMachinesLoading(true);
    try {
      const response = await apiMachineMachinesGet();
      if (response && response.status === 200) {
        setMachines(Array.isArray(response.data) ? response.data : []);
      } else {
        setMachines([]);
      }
    } catch {
      setMachines([]);
    }
    setMachinesLoading(false);
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await apiMachineStatsGet();
      if (response?.status === 200 && response.data != null && typeof response.data === "object" && "activeMachines" in response.data) {
        setStats(response.data as MachineStats);
      } else {
        setStats(null);
      }
    } catch {
      setStats(null);
    }
    setStatsLoading(false);
  };

  const handleSaveName = async (machine: MachineInfo) => {
    if (!editName.trim()) {
      setEditingId(null);
      return;
    }
    setSaving(true);
    try {
      const response = await apiMachineNamePost({ machineCode: machine.machineId, name: editName.trim() });
      if (response && response.status === 200) {
        setMachines((prev) =>
          prev.map((m) =>
            m.machineId === machine.machineId
              ? { ...m, friendlyName: editName.trim() }
              : m,
          ),
        );
        setEditingId(null);
        await fetchMachines();
      } else if (response?.status === 403) {
        toastContext.createToast(UnauthorizedSaveErrorToast);
      } else {
        toastContext.createToast(SaveErrorToast);
      }
    } catch {
      toastContext.createToast(SaveExceptionToast);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMachine = async (machine: MachineInfo) => {
    setRemoving(machine.machineId);
    try {
      const authResponse = await apiMachineAuthorizeGet();
      if (authResponse?.status === 403) {
        toastContext.createToast(RemoveAuthErrorToast);
        return;
      }

      if (!window.confirm(`Are you sure you want to remove "${machine.friendlyName}"?`)) {
        return;
      }

      const response = await apiMachineMachineDelete(machine.machineId);
      if (response && response.status === 200) {
        setMachines((prev) => prev.filter((m) => m.machineId !== machine.machineId));
        await fetchStats();
      } else {
        toastContext.createToast(RemoveErrorToast);
      }
    } finally {
      setRemoving(null);
    }
  };

  const handleStartEdit = (machine: MachineInfo) => {
    setEditingId(machine.machineId);
    setEditName(machine.friendlyName);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName("");
  };

  useEffect(() => {
    void Promise.all([fetchMachines(), fetchStats()]);
  }, []);

  const handleKeyDown = (machine: MachineInfo, e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter") {
      void handleSaveName(machine);
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  const cardSx = {
    background: COLORS.cardBg,
    border: `1px solid ${COLORS.line}`,
    borderRadius: '12px',
  };

  const renderStatsSkeleton = () => (
    <Grid container spacing={3}>
      {[0, 1].map((i) => (
        <Grid key={`stat-skeleton-${i}`} size={6}>
          <Card sx={{ ...cardSx, p: '20px 22px', maxWidth: 620 }}>
            <Skeleton variant="text" width="50%" height={24} sx={{ bgcolor: 'rgba(255,255,255,.1)' }} />
            <Skeleton variant="text" width="40%" height={48} sx={{ mt: 1, bgcolor: 'rgba(255,255,255,.1)' }} />
            <Skeleton variant="rectangular" height={7} sx={{ mt: 2, borderRadius: 99, bgcolor: 'rgba(255,255,255,.1)' }} />
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderMachineSkeleton = () => (
    <Stack spacing={1}>
      {[0, 1, 2].map((i) => (
        <Card key={`machine-skeleton-${i}`} sx={{ ...cardSx, p: '16px 20px' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 4 }}>
              <Skeleton variant="text" height={24} sx={{ bgcolor: 'rgba(255,255,255,.1)' }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Skeleton variant="text" height={20} sx={{ bgcolor: 'rgba(255,255,255,.1)' }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Skeleton variant="text" height={20} sx={{ bgcolor: 'rgba(255,255,255,.1)' }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <Skeleton variant="rectangular" height={36} width={190} sx={{ borderRadius: 7, bgcolor: 'rgba(255,255,255,.1)' }} />
            </Grid>
          </Grid>
        </Card>
      ))}
    </Stack>
  );

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: `
        radial-gradient(900px 600px at 90% -10%, rgba(45,107,98,.45), transparent 60%),
        radial-gradient(700px 500px at -10% 110%, rgba(45,107,98,.45), transparent 60%),
        #1A1A1A`,
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 3, md: 7 }, py: 3, borderBottom: `1px solid ${COLORS.line}` }}>
        <Box component="img" src={logoWhite} alt="DigitalCNC" sx={{ height: 36, display: 'block' }} />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Link component={RouterLink} to="/products" sx={{ fontSize: 14, fontWeight: 500, textDecoration: 'none', px: 2, py: 1, borderRadius: 8, border: `1px solid rgba(255,255,255,.28)`, color: COLORS.white, transition: 'background .15s,color .15s,border-color .15s', '&:hover': { borderColor: COLORS.yellow, color: COLORS.yellow } }}>
            Licences
          </Link>
          <Box sx={{ fontSize: 14, fontWeight: 500, px: 2, py: 1, borderRadius: 8, backgroundColor: COLORS.yellow, color: '#1A1A1A' }}>
            Machines
          </Box>
          <Link component={RouterLink} to="/login" sx={{ fontSize: 14, color: COLORS.grey, textDecoration: 'none', pl: 1, transition: 'color .15s', '&:hover': { color: COLORS.white } }}>
            Sign out
          </Link>
        </Box>
      </Box>

      {/* Main content */}
      <Container sx={{ flex: 1, maxWidth: 'min(1120px, 100%)', py: { xs: 4, md: 6 } }}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: 'clamp(26px,3vw,34px)', mb: 1 }}>
          Machines
        </Typography>
        <Typography sx={{ color: COLORS.grey, fontSize: 15, mb: 4, lineHeight: 1.5 }}>
          Each licence seat is bound to a machine. Manage which devices are activated, free up seats, and keep an eye on utilisation.
        </Typography>

        {/* Stats Section */}
        {statsLoading ? (
          renderStatsSkeleton()
        ) : stats != null && Object.keys(stats).length > 0 ? (
          <Grid container spacing={3} sx={{ mb: 5, maxWidth: 620 }}>
            <Grid size={6}>
              <Card sx={{ ...cardSx, p: '20px 22px' }}>
                <Typography variant="body2" sx={{ color: COLORS.grey, letterSpacing: '.03em', mb: 1 }}>
                  Active machines
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, lineHeight: 1 }}>
                  {stats.activeMachines} <Typography component="span" variant="h6" sx={{ color: COLORS.grey, fontWeight: 400, fontSize: 18 }}>/ {stats.maxMachines}</Typography>
                </Typography>
              </Card>
            </Grid>
            <Grid size={6}>
              <Card sx={{ ...cardSx, p: '20px 22px' }}>
                <Typography variant="body2" sx={{ color: COLORS.grey, letterSpacing: '.03em', mb: 1 }}>
                  Utilisation
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, lineHeight: 1 }}>
                  {stats.utilizationPercentage}%
                </Typography>
                <Box sx={{ mt: 2, height: 7, borderRadius: 99, backgroundColor: 'rgba(255,255,255,.10)', overflow: 'hidden' }}>
                  <Box sx={{ display: 'block', height: '100%', width: `${stats.utilizationPercentage}%`, borderRadius: 99, background: `linear-gradient(90deg, ${COLORS.yellow}, #cdd400)` }} />
                </Box>
              </Card>
            </Grid>
          </Grid>
        ) : null}

        {/* Machines Section */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 19 }}>
            Activated machines
          </Typography>
          <When expr={stats != null && Object.keys(stats).length > 0}>
            <Typography sx={{ fontSize: 13, color: COLORS.grey }}>
              {stats?.activeMachines} of {stats?.maxMachines} seats in use
            </Typography>
          </When>
        </Box>

        {/* Column headers */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 150px 130px 190px' }, gap: 2, alignItems: 'center', px: { xs: 0, sm: 2 }, pb: 0.5, fontSize: '11.5px', letterSpacing: '.06em', textTransform: 'uppercase', color: '#6F757B' }}>
          <span>Machine</span>
          <span>IP address</span>
          <span>Last active</span>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }} />
        </Box>

        <When expr={machinesLoading}>
          {renderMachineSkeleton()}
        </When>
        <When expr={!machinesLoading && Array.isArray(machines) && machines.length > 0}>
          <Stack spacing={1}>
            {machines.map((machine) => (
              <Card key={machine.machineId} sx={{ ...cardSx, p: '16px 20px', '&:hover': { borderColor: 'rgba(235,227,45,.30)' } }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 150px 130px 190px' }, gap: 2, alignItems: 'center' }}>
                  <Box>
                    <When expr={editingId === machine.machineId}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                          size="small"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => handleKeyDown(machine, e)}
                          autoFocus
                          fullWidth
                          InputProps={{ sx: { color: COLORS.white, fontSize: 14.5 } }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => void handleSaveName(machine)}
                          disabled={saving}
                          sx={{ backgroundColor: COLORS.yellow, color: '#1A1A1A', fontWeight: 500, borderRadius: 7, fontSize: 13 }}
                        >
                          {saving ? "..." : "Save"}
                        </Button>
                        <Button size="small" onClick={handleCancelEdit} sx={{ color: COLORS.lead, borderColor: COLORS.line }}>
                          Cancel
                        </Button>
                      </Stack>
                    </When>
                    <When expr={editingId !== machine.machineId}>
                      <Typography variant="body1" fontWeight={500} sx={{ color: COLORS.white, fontSize: 14.5, wordBreak: 'break-word' }}>
                        {machine.friendlyName}
                      </Typography>
                    </When>
                  </Box>
                  <Typography variant="body2" sx={{ color: COLORS.grey, fontSize: 13.5 }}>
                    {machine.ipAddress}
                  </Typography>
                  <Typography variant="body2" sx={{ color: COLORS.grey, fontSize: 13.5 }}>
                    {machine.lastSeen ? new Date(machine.lastSeen).toLocaleDateString() : "Never"}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                    <When expr={editingId !== machine.machineId}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleStartEdit(machine)}
                        sx={{ color: '#5A5A5A', borderColor: 'rgba(255,255,255,.18)', borderRadius: 7, fontSize: 13, fontWeight: 500, '&:hover': { borderColor: COLORS.yellow, color: COLORS.yellow } }}
                      >
                        Rename
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => void handleRemoveMachine(machine)}
                        disabled={removing === machine.machineId}
                        sx={{ color: COLORS.danger, borderColor: `rgba(${229},115,107,.5)`, borderRadius: 7, fontSize: 13, fontWeight: 500, '&:hover': { backgroundColor: 'rgba(229,115,107,.12)' } }}
                      >
                        Remove
                      </Button>
                    </When>
                  </Box>
                </Box>
              </Card>
            ))}
          </Stack>
        </When>
        <When expr={!machinesLoading && machines.length === 0}>
          <Typography sx={{ mt: 2, color: COLORS.grey }} align="center">
            No machines are currently activated under this licence key. Activate a machine to see it here.
          </Typography>
        </When>

        {/* Hint box */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2, alignItems: 'flex-start', fontSize: 13, color: COLORS.grey, background: 'rgba(255,255,255,.03)', border: `1px solid ${COLORS.line}`, borderRadius: 2, p: '14px 18px' }}>
          <Box component="svg" viewBox="0 0 16 16" fill="none" aria-hidden sx={{ flex: '0 0 16px', width: 16, height: 16, mt: '1px' }}>
            <circle cx="8" cy="8" r="6.5" stroke={COLORS.grey} strokeWidth="1.2" />
            <path d="M8 7.2v3.4M8 5.1v.1" stroke={COLORS.grey} strokeWidth="1.4" strokeLinecap="round" />
          </Box>
          <span>Removing a machine frees its seat immediately so you can activate a different device. Need more seats? <Box component="a" href="mailto:support@digitalcnc.ai" sx={{ color: COLORS.yellow, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>support@digitalcnc.ai</Box></span>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ borderTop: `1px solid ${COLORS.line}`, mt: 5, px: { xs: 3, md: 7 }, py: 2.5, display: 'flex', flexWrap: 'wrap', gap: '10px 28px', alignItems: 'center', justifyContent: 'space-between', fontSize: '12.5px', color: COLORS.grey }}>
        <span>Copyright © 2026 DigitalCNC | Yorkshire AI Labs</span>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box component="a" href="mailto:support@digitalcnc.ai" sx={{ color: COLORS.lead, textDecoration: 'none', '&:hover': { color: COLORS.white } }}>Support</Box>
          <Box component="a" href="https://digitalcnc.ai/privacy-policy/" sx={{ color: COLORS.lead, textDecoration: 'none', '&:hover': { color: COLORS.white } }}>Privacy Policy</Box>
          <Box component="a" href="https://www.linkedin.com/company/digitalcnc" rel="noopener" sx={{ color: COLORS.lead, textDecoration: 'none', '&:hover': { color: COLORS.white } }}>LinkedIn</Box>
          <Box component="a" href="https://digitalcnc.ai" sx={{ color: COLORS.lead, textDecoration: 'none', '&:hover': { color: COLORS.white } }}>digitalcnc.ai</Box>
        </Box>
      </Box>
    </Box>
  );
};
